import { FilterPill } from "@/design/primitives";

/**
 * Filtr sedí na všech čtyřech obrazovkách (rozhodnutí, úkoly, blokace,
 * návrhy), takže je psaný na tvar, ne na konkrétní DTO — jediné, co potřebuje,
 * je projekt a naléhavost.
 */
export interface FilterableItem {
  projectId: string | null;
  projectName: string | null;
  urgency?: string;
}

/** Klíč pseudo-projektu pro položky bez projektu (Factory-level rozhodnutí). */
export const FACTORY_KEY = "__factory";
export const ALL_PROJECTS = "ALL";

export interface ProjectGroup {
  key: string;
  label: string;
  count: number;
  /** Aspoň jedno URGENT rozhodnutí — projekt si zaslouží tečku, i když zrovna není vybraný. */
  urgent: boolean;
}

const groupKey = (item: FilterableItem) => item.projectId ?? FACTORY_KEY;

/** Skupiny pro sub-navigaci: abecedně, Factory (bez projektu) až na konci, ať chipy neskáčou. */
export function projectGroups(items: FilterableItem[]): ProjectGroup[] {
  const map = new Map<string, ProjectGroup>();
  for (const item of items) {
    const key = groupKey(item);
    const existing = map.get(key);
    if (existing) {
      existing.count += 1;
      existing.urgent = existing.urgent || item.urgency === "URGENT";
    } else {
      map.set(key, { key, label: item.projectName ?? "Factory", count: 1, urgent: item.urgency === "URGENT" });
    }
  }
  return [...map.values()].sort((a, b) => {
    if (a.key === FACTORY_KEY) return 1;
    if (b.key === FACTORY_KEY) return -1;
    return a.label.localeCompare(b.label, "cs");
  });
}

export function filterByProject<T extends FilterableItem>(items: T[], key: string): T[] {
  if (key === ALL_PROJECTS) return items;
  return items.filter((i) => groupKey(i) === key);
}

/** Vybraný projekt může zmizet (poslední rozhodnutí vyřešeno) → spadneme zpět na „vše“. */
export function resolveProjectFilter(groups: ProjectGroup[], key: string): string {
  return key !== ALL_PROJECTS && groups.some((g) => g.key === key) ? key : ALL_PROJECTS;
}

/**
 * Sub-navigace inboxu podle projektů. Zobrazí se až od dvou skupin —
 * u jediného projektu by to byl jen šum.
 */
export function ProjectFilterTabs({
  items,
  value,
  onChange,
  className = "",
  testIdPrefix = "inbox",
}: {
  items: FilterableItem[];
  value: string;
  onChange: (key: string) => void;
  className?: string;
  testIdPrefix?: string;
}) {
  const groups = projectGroups(items);
  if (groups.length < 2) return null;

  const tabs: ProjectGroup[] = [{ key: ALL_PROJECTS, label: "Vše", count: items.length, urgent: false }, ...groups];

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`} data-testid={`${testIdPrefix}-project-filter`}>
      {tabs.map((tab) => {
        const active = tab.key === value;
        return (
          <FilterPill
            key={tab.key}
            active={active}
            onClick={() => onChange(tab.key)}
            testId={`${testIdPrefix}-project-${tab.key}`}
          >
            <span className="flex items-center gap-2 whitespace-nowrap">
              {tab.urgent ? (
                <span
                  aria-hidden
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 6,
                    background: "var(--critical)",
                    flexShrink: 0,
                  }}
                />
              ) : null}
              {tab.label}
              <span style={{ opacity: 0.6 }}>· {tab.count}</span>
            </span>
          </FilterPill>
        );
      })}
    </div>
  );
}
